import { COLOR_NAMES } from './colorNames.js';

// Parses "#fff", "fff", "#ffffff" or "ffffff" into { r, g, b }. Returns null
// if the input isn't a valid hex color.
export function hexToRgb(hex) {
  if (typeof hex !== 'string') return null;
  let h = hex.trim().replace(/^#/, '');
  if (h.length === 3) {
    h = h.split('').map((c) => c + c).join('');
  }
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

// The inverse of hexToRgb - { r, g, b } -> "#RRGGBB".
export function rgbToHex({ r, g, b }) {
  const toHex = (n) => n.toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
}

// "Redmean" weighted Euclidean distance: a cheap approximation of perceptual
// color difference that tracks how the eye judges similarity noticeably
// better than plain RGB distance, without needing a full Lab-space conversion.
export function colorDistance(a, b) {
  const rMean = (a.r + b.r) / 2;
  const dr = a.r - b.r;
  const dg = a.g - b.g;
  const db = a.b - b.b;
  const weightR = 2 + rMean / 256;
  const weightG = 4;
  const weightB = 2 + (255 - rMean) / 256;
  return Math.sqrt(weightR * dr * dr + weightG * dg * dg + weightB * db * db);
}

// Distance between pure black and white under the metric above - used to
// turn a raw distance into a 0-100% similarity score.
const MAX_DISTANCE = colorDistance({ r: 0, g: 0, b: 0 }, { r: 255, g: 255, b: 255 });

// Returns the closest `count` named colors to `hex`, nearest first. Each
// entry carries a 0-100 `similarity` score and an `isExactMatch` flag.
// Pass your own `palette` (same { name, hex } shape) to match against a
// custom set of names instead of the CSS defaults.
export function getColorMatches(hex, count = 5, palette = COLOR_NAMES) {
  const target = hexToRgb(hex);
  if (!target) return [];

  return palette
    .map(({ name, hex: paletteHex }) => {
      const distance = colorDistance(target, hexToRgb(paletteHex));
      return {
        name,
        hex: paletteHex,
        distance,
        isExactMatch: distance === 0,
        similarity: Math.max(0, Math.round((1 - distance / MAX_DISTANCE) * 1000) / 10),
      };
    })
    .sort((a, b) => a.distance - b.distance)
    .slice(0, count);
}
